import { useState } from "react";
import { AlertTriangle, Calendar, Check, Clock, Images, MapPin, X } from "lucide-react";
import { Lead } from "../../types/lead";
import { MediaViewer } from "../../components/MediaViewer";
import { PartnerName } from "../../components/reviews/PartnerReview";

// Optional fields returned by the jobs API alongside the shared lead model.
type AvailableLead = Omit<Lead, "status"> & {
  status: string;
  tradesmens_commission?: number | string | null;
  lead_sub_cat?: { id?: number | string; name: string }[];
};
interface Props {
  lead: AvailableLead;
  requested?: boolean;
  history?: boolean;
  onViewDetails: (id: string) => void;
  onApplyLead?: (id: string) => void;
  onDeclineLead?: (id: string) => void;
}
const money = (value: number | string | null | undefined) =>
  value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? "Not provided" :
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(value));

export function AvailableLeadCard({ lead, onViewDetails, onApplyLead, onDeclineLead, requested = false, history = false }: Props) {
  const [showMedia, setShowMedia] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [failedProfileImage, setFailedProfileImage] = useState<string | null>(null);
  const profileImage = lead.homeowner?.pic || lead.homeowner?.profileImage;
  const media = lead.media?.length ? lead.media : (lead.images || []).map(url => ({ type: "image" as const, url }));
  const preview = media[0]?.type === "image" ? media[0].url : media[0]?.thumbnail;
  const name = lead.homeowner?.name || "Homeowner";
  const historyLead = lead as AvailableLead & { completedDate?: string; declinedDate?: string; disputedDate?: string; cancelledDate?: string; notes?: string };
  const historyDate = historyLead.completedDate || historyLead.declinedDate || historyLead.disputedDate || historyLead.cancelledDate;
  const date = new Date((history && historyDate) || (requested && lead.requestedDate) || lead.postedDate);
  const StatusIcon = requested ? Clock : history && lead.status === "dispute" ? AlertTriangle : history && ["declined", "cancelled"].includes(lead.status) ? X : Check;
  const posted = Number.isNaN(date.getTime()) ? "Date not provided" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return <>
    <article className="available-lead-record" aria-label={lead.title}>
      <button type="button" className="available-lead-thumbnail" onClick={() => media.length ? setShowMedia(true) : onViewDetails(lead.id)}
        aria-label={media.length ? `View media for ${lead.title}` : `View details for ${lead.title}`}>
        {preview && !imageFailed ? <img src={preview} alt="" loading="lazy" onError={() => setImageFailed(true)} /> : <Images size={28} aria-hidden="true" />}
        {media.length > 0 && <span><Images size={12} aria-hidden="true" />{media.length} {media.length === 1 ? "item" : "items"}</span>}
      </button>
      <div className="available-lead-main">
        <div className="available-lead-top">
          <div className="available-lead-context">
            <span className={`available-lead-status${history ? ` available-lead-status-${lead.status}` : ""}${requested ? " available-lead-status-requested" : ""}`}><StatusIcon size={12} aria-hidden="true" />{requested ? "Requested" : lead.status === "open" ? "Open" : lead.status === "dispute" ? "Disputed" : lead.status}</span>
            <span className="available-lead-reference">Lead #{lead.id}</span>
          </div>
          <dl className="available-lead-pricing">
            <div><dt>Budget</dt><dd>{money(lead.budget)}</dd></div>
            {!history && <div className="available-lead-acceptance-fee"><dt>Acceptance fee</dt><dd>{money(lead.tradesmens_commission)}</dd></div>}
          </dl>
        </div>
        <div className="available-lead-heading"><h2><button type="button" onClick={() => onViewDetails(lead.id)}>{lead.title}</button></h2></div>
        {lead.description && <p className="available-lead-description">{lead.description}</p>}
        <div className="available-lead-meta">
          <span><MapPin size={14} aria-hidden="true" />{[lead.location?.city, lead.location?.state].filter(Boolean).join(", ") || "Location not provided"}</span>
          <span><Calendar size={14} aria-hidden="true" />{history && historyDate ? "Updated" : requested && lead.requestedDate ? "Requested" : "Posted"} {posted}</span>
        {!!lead.lead_sub_cat?.length && <span className="available-lead-services">{lead.lead_sub_cat.slice(0, 2).map(service => service.name).join(" · ")}
          {lead.lead_sub_cat.length > 2 && ` +${lead.lead_sub_cat.length - 2} more`}</span>}
        </div>
        {history && historyLead.notes && <p className="history-lead-notes"><strong>Notes:</strong> {historyLead.notes}</p>}
        <footer className="available-lead-footer">
          <div className="available-lead-person">
            <PartnerName id={lead.homeowner?.id}>
            <span className="available-lead-avatar" aria-hidden="true">
              {profileImage && failedProfileImage !== profileImage ?
                <img src={profileImage} alt="" loading="lazy" onError={() => setFailedProfileImage(profileImage)} /> :
                name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("")}
            </span>
            <span className="available-lead-person-copy"><span className="available-lead-person-label">Homeowner</span><span className="available-lead-person-name">{name}</span></span>
            </PartnerName>
          </div>
          <div className="available-lead-actions">
          <button type="button" className={`available-lead-button${history ? " available-lead-button-primary" : ""}`} onClick={() => onViewDetails(lead.id)}>View details</button>
          {onApplyLead && <button type="button" className="available-lead-button available-lead-button-primary" onClick={() => onApplyLead(lead.id)}>Accept lead</button>}
          {onDeclineLead && <button type="button" className="available-lead-button available-lead-button-decline" onClick={() => onDeclineLead(lead.id)}><X size={14} aria-hidden="true" />Decline</button>}
          </div>
        </footer>
      </div>
    </article>
    {showMedia && <MediaViewer mediaItems={media} onClose={() => setShowMedia(false)} />}
  </>;
}
